/**
 * Naming the day's island.
 *
 * A name is generated once, from the day's seed, as a **structure**: which
 * pattern was drawn and which index each of its slots landed on. The structure
 * is what the day row stores (`game_days.island_name_json`), and every locale
 * renders it from its own parallel lists, so one island has one identity and
 * each player reads it in their own language (Q12.4).
 *
 * The English rendering is stored beside it and is the uniqueness key in
 * `used_island_names`: a translated list could collide where English does not,
 * and an island that shares a name with a past island is confusing in the Atlas
 * whatever language it is read in.
 */

import { createRng, type Rng } from '../rng.ts'
import type { IslandNamesFile } from '../content/schemas.ts'

/** A generated name, as stored. Slot values are indices into the locale lists. */
export type IslandNameStructure = {
  /** The `patterns[].id` that was drawn. */
  readonly pattern: string
  /** Slot name in the pattern template to the index it drew. */
  readonly slots: Readonly<Record<string, number>>
}

export type GeneratedIslandName = {
  readonly structure: IslandNameStructure
  /** The English rendering, which is the uniqueness key. */
  readonly english: string
}

/** Rejection attempts before the name space is declared exhausted. */
const MAX_ATTEMPTS = 1000

/** The values a slot may draw from, in a locale's lists. */
function slotValues(slot: string, lists: IslandNamesFile['lists']): readonly string[] {
  switch (slot) {
    case 'noun':
      return lists.noun.map((entry) => entry.singular)
    case 'nounPlural':
      return lists.noun.map((entry) => entry.plural)
    case 'adjective':
    case 'saint':
    case 'eventPhrase':
    case 'compoundFirst':
    case 'compoundSecond':
    case 'numberWord':
      return lists[slot]
    default:
      throw new Error(`island-names.json names a list that does not exist: {${slot}}`)
  }
}

/** Slot names a pattern template draws, in the order they appear. */
export function patternSlots(template: string): string[] {
  return [...template.matchAll(/\{([A-Za-z0-9_]+)\}/g)].map((match) => match[1]!)
}

/**
 * A regular expression matching exactly the names one pattern can produce.
 * Used by the content test to check `examples[]` and by the naming test to
 * check that a rendering really belongs to the pattern it claims.
 */
export function patternMatcher(template: string, lists: IslandNamesFile['lists']): RegExp {
  const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const source = template.replace(
    /\{([A-Za-z0-9_]+)\}|([^{]+)/g,
    (_all, slot?: string, literal?: string) =>
      slot === undefined
        ? escape(literal ?? '')
        : `(?:${slotValues(slot, lists).map(escape).join('|')})`,
  )
  return new RegExp(`^${source}$`)
}

/** Render a stored structure from one locale's lists. */
export function renderIslandName(structure: IslandNameStructure, file: IslandNamesFile): string {
  const pattern = file.patterns.find((candidate) => candidate.id === structure.pattern)
  if (pattern === undefined) {
    throw new Error(`island-names.json has no pattern "${structure.pattern}"`)
  }
  return pattern.template.replace(/\{([A-Za-z0-9_]+)\}/g, (_all, slot: string) => {
    const values = slotValues(slot, file.lists)
    const drawn = structure.slots[slot]
    if (drawn === undefined) throw new Error(`the name has no value for {${slot}}`)
    // Lists are parallel by contract, but a locale that has drifted should
    // still render something rather than throw at a player.
    return values[drawn] ?? values[drawn % values.length] ?? ''
  })
}

function drawStructure(rng: Rng, file: IslandNamesFile): IslandNameStructure {
  const pattern = rng.weighted(file.patterns, (candidate) => candidate.weight)
  const slots: Record<string, number> = {}
  for (const slot of patternSlots(pattern.template)) {
    slots[slot] = rng.int(slotValues(slot, file.lists).length)
  }
  return { pattern: pattern.id, slots }
}

/**
 * Draw the name for a seed, skipping names already used.
 *
 * `english` is the file passed in, which must be the source locale: the
 * uniqueness key is the English rendering, so the exclusion set only means
 * anything against English.
 */
export function islandName(
  seed: Uint8Array,
  english: IslandNamesFile,
  usedNames: ReadonlySet<string> = new Set(),
): GeneratedIslandName {
  const rng = createRng(seed)
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const structure = drawStructure(rng, english)
    const rendered = renderIslandName(structure, english)
    if (!usedNames.has(rendered)) return { structure, english: rendered }
  }
  // Roughly 39 000 names exist, so this is a century of islands rather than a
  // run of bad luck. It is thrown rather than swallowed so it is noticed.
  throw new Error('every island name the patterns can produce has been used')
}
